import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { orders as ordersApi } from '../api/endpoints.ts';
import type { OrderListItem } from '../api/types.ts';
import { formatRsd } from '../core/money.ts';
import { OrderStatusGroup } from '../core/order-status.ts';
import { arrivalText, errorText, orderMoment, statusText, useLang, useStrings } from '../i18n/index.ts';
import { pushRoute } from '../state/router.ts';
import { useWatcher } from '../state/watcher.ts';
import { Icon } from '../ui/icons.tsx';
import { cx, EmptyState, ErrorBanner, ScreenTitle, Skeleton } from '../ui/kit.tsx';
import { Screen, ScreenPlaceContext } from '../ui/Screen.tsx';
import { orderRowIcon, orderTint } from './OrderParts.tsx';

const POLL_MS = 10_000;

/** Заказы: сверху то, что ещё в пути, ниже история. */
export function OrdersScreen() {
  const strings = useStrings();
  const { orderVersion } = useWatcher();
  const { covered } = useContext(ScreenPlaceContext);

  const [items, setItems] = useState<OrderListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const stringsRef = useRef(strings);
  stringsRef.current = strings;

  const load = useCallback(async () => {
    try {
      const response = await ordersApi.list();
      setItems(response.items);
      setError(null);
    } catch (cause) {
      setError(errorText(stringsRef.current, cause));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Список живой, пока экран сверху и вкладка на виду: событие заказа перечитывает его сразу,
  // таймер страхует от пропущенного события.
  useEffect(() => {
    if (covered) return;
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [covered, orderVersion, load]);

  const active = items.filter((item) => OrderStatusGroup.isActive(item.status));
  const history = items.filter((item) => !OrderStatusGroup.isActive(item.status));

  return (
    <Screen>
      <div className="readable stack orders-screen">
        <ScreenTitle text={strings.ordersTitle} />

        {error && (
          <div className="gutter">
            <ErrorBanner text={error} onRetry={() => void load()} />
          </div>
        )}

        {isLoading && items.length === 0 && (
          <div className="gutter stack orders-list">
            <Skeleton height={74} radius={20} />
            <Skeleton height={74} radius={20} />
            <Skeleton height={74} radius={20} />
          </div>
        )}

        {items.length === 0 && !isLoading && !error ? (
          <div className="orders-empty">
            <EmptyState image="/empty/orders.webp" title={strings.noOrders} hint={strings.noOrdersHint} />
          </div>
        ) : (
          <>
            <OrdersSection title={strings.activeOrder} items={active} />
            <OrdersSection title={strings.historyTitle} items={history} />
          </>
        )}
      </div>
    </Screen>
  );
}

function OrdersSection({ title, items }: { title: string; items: OrderListItem[] }) {
  if (items.length === 0) return null;
  return (
    <section className="stack orders-section">
      <h2 className="gutter t-display">{title}</h2>
      <div className="gutter stack orders-list">
        {items.map((item) => (
          <OrderRow key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

/** Строка заказа: момент оформления, сумма, статус, обещанное время и число позиций. */
function OrderRow({ item }: { item: OrderListItem }) {
  const strings = useStrings();
  const lang = useLang();
  const active = OrderStatusGroup.isActive(item.status);
  const tint = orderTint(item.status);
  const details = [arrivalText(lang, strings, item.status, item.expectedAt), strings.itemsCount(item.itemsCount)]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <button type="button" className="order-row pressable" onClick={() => pushRoute({ name: 'order', orderId: item.id })}>
      <span className={cx('order-row-icon', active && 'is-active')} style={{ color: tint }}>
        <Icon name={orderRowIcon(item.status)} size={20} weight={2.4} />
      </span>
      <span className="grow stack order-row-text">
        <span className="row order-row-head">
          <b className="grow">{orderMoment(lang, item.placedAt)}</b>
          <b className="t-numeric">{formatRsd(item.totalRsd)}</b>
        </span>
        <span className="row order-row-sub">
          <b style={{ color: tint }}>{statusText(strings, item.status)}</b>
          <span className="t-muted">·</span>
          <span className="t-muted order-row-details">{details}</span>
        </span>
      </span>
      <Icon name="chevronRight" size={14} weight={2.8} className="order-chevron" />
    </button>
  );
}
